import {
  Component,
  EventEmitter,
  Output,
  ViewChildren,
  QueryList,
  ElementRef,
  Input,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-otp-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="flex gap-3 justify-center">
      @for (digit of digits; track $index) {
        <input
          #digitInput
          type="text"
          inputmode="numeric"
          maxlength="1"
          class="w-12 h-14 text-center text-xl font-medium border rounded-lg
                 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                 transition-colors"
          [class.border-outline-variant]="!showError"
          [class.border-error]="showError"
          [class.text-error]="showError"
          [class.shake]="showError"
          [disabled]="isLocked"
          [value]="digits[$index]"
          (input)="onDigitInput($event, $index)"
          (keydown)="onKeyDown($event, $index)"
          (paste)="onPaste($event)"
          [attr.data-testid]="'otp-digit-' + $index"
        />
      }
    </div>
  `,
  styles: [`
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
      20%, 40%, 60%, 80% { transform: translateX(4px); }
    }
    .shake {
      animation: shake 0.5s ease-in-out;
    }
    input:disabled {
      opacity: 1;
    }
  `],
})
export class OtpInputComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChildren('digitInput') digitInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @Output() codeComplete = new EventEmitter<string>();
  @Input() hasError = false;

  digits: string[] = ['', '', '', '', '', ''];
  showError = false;
  isLocked = false;
  private errorResetTimer: any;

  ngAfterViewInit(): void {
    setTimeout(() => this.focusDigit(0), 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hasError']?.currentValue === true) {
      this.triggerErrorState();
    }
  }

  ngOnDestroy(): void {
    if (this.errorResetTimer) clearTimeout(this.errorResetTimer);
  }

  /** Show error animation, clear fields, then re-enable after 750ms (matches Flutter). */
  private triggerErrorState(): void {
    this.showError = true;
    this.isLocked = true;

    // Clear after a short delay so the user sees their wrong code flash red
    setTimeout(() => {
      this.digits = ['', '', '', '', '', ''];
      const inputs = this.digitInputs?.toArray();
      inputs?.forEach(i => (i.nativeElement.value = ''));
    }, 300);

    // Reset error state and re-enable input after 750ms
    if (this.errorResetTimer) clearTimeout(this.errorResetTimer);
    this.errorResetTimer = setTimeout(() => {
      this.showError = false;
      this.isLocked = false;
      setTimeout(() => this.focusDigit(0), 50);
    }, 750);
  }

  clear(): void {
    this.digits = ['', '', '', '', '', ''];
    const inputs = this.digitInputs.toArray();
    inputs.forEach(i => (i.nativeElement.value = ''));
    setTimeout(() => this.focusDigit(0), 50);
  }

  onDigitInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Clear error as soon as user starts typing
    if (this.showError) {
      this.showError = false;
    }

    if (value && !/^\d$/.test(value)) {
      this.digits[index] = '';
      input.value = '';
      return;
    }

    if (value) {
      this.digits[index] = value;
      if (index < 5) {
        this.focusDigit(index + 1);
      }
      this.checkComplete();
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace') {
      if (!input.value && index > 0) {
        this.digits[index] = '';
        this.focusDigit(index - 1);
      } else if (input.value) {
        this.digits[index] = '';
      }
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusDigit(index - 1);
    } else if (event.key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusDigit(index + 1);
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digitsOnly = pastedData.replace(/\D/g, '').slice(0, 6);
    if (digitsOnly.length > 0) {
      for (let i = 0; i < 6; i++) {
        this.digits[i] = digitsOnly[i] || '';
      }
      const inputs = this.digitInputs.toArray();
      inputs.forEach((inp, i) => (inp.nativeElement.value = this.digits[i]));
      const nextIdx = Math.min(digitsOnly.length, 5);
      this.focusDigit(nextIdx);
      this.checkComplete();
    }
  }

  private focusDigit(index: number): void {
    const inputs = this.digitInputs?.toArray();
    if (inputs?.[index]) {
      inputs[index].nativeElement.focus();
    }
  }

  private checkComplete(): void {
    const code = this.digits.join('');
    if (code.length === 6 && /^\d{6}$/.test(code)) {
      this.codeComplete.emit(code);
    }
  }
}
